"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusAutoGenerator = void 0;
/**
 * Nexus自动生成器
 * 真正的零配置：用户只写TypeScript，框架自动处理一切
 */
class NexusAutoGenerator {
    static startWatchMode(sourceDir, outputDir) {
        console.log("🔄 Nexus监听模式启动");
        console.log(`📂 源码目录: ${sourceDir}`);
        console.log(`📁 输出目录: ${outputDir}`);
        // 初始生成
        this.generateAll(sourceDir, outputDir);
        const fs = require('fs');
        const path = require('path');
        // 记录当前存在的文件
        let currentFiles = new Set();
        const updateCurrentFiles = () => {
            currentFiles.clear();
            const files = this.getTypeScriptFiles(sourceDir);
            files.forEach(file => currentFiles.add(file));
        };
        updateCurrentFiles();
        if (fs.existsSync(sourceDir)) {
            fs.watch(sourceDir, { recursive: true }, (eventType, filename) => {
                if (!filename || !filename.endsWith('.ts'))
                    return;
                const fullPath = path.join(sourceDir, filename);
                const fileExists = fs.existsSync(fullPath);
                console.log(`📝 检测到文件变化: ${eventType} - ${filename}`);
                if (eventType === 'rename' || eventType === 'change') {
                    if (!fileExists && currentFiles.has(fullPath)) {
                        // 文件被删除
                        console.log(`🗑️  文件已删除: ${filename}`);
                        this.handleFileDeleted(fullPath, sourceDir, outputDir);
                        currentFiles.delete(fullPath);
                    }
                    else if (fileExists && !currentFiles.has(fullPath)) {
                        // 新文件创建
                        console.log(`📄 新文件创建: ${filename}`);
                        currentFiles.add(fullPath);
                        this.generateAll(sourceDir, outputDir);
                    }
                    else if (fileExists) {
                        // 文件修改
                        console.log(`✏️  文件已修改: ${filename}`);
                        this.generateAll(sourceDir, outputDir);
                    }
                }
            });
        }
    }
    /**
     * 处理文件删除事件
     */
    static handleFileDeleted(deletedFilePath, sourceDir, outputDir) {
        const fs = require('fs');
        const path = require('path');
        try {
            // 检查删除的文件是否是插件或面板文件
            const relativePath = path.relative(sourceDir, deletedFilePath);
            const generatedDir = path.join(outputDir, 'generated');
            // 如果是插件文件，可能需要删除对应的main.ts
            if (relativePath.includes('plugin') || relativePath.includes('Plugin')) {
                console.log(`🧹 清理插件相关文件...`);
                // 重新生成所有文件，让系统自动清理不再需要的文件
                this.generateAll(sourceDir, outputDir);
                return;
            }
            // 如果是面板文件，删除对应的生成文件
            if (relativePath.includes('panel') || relativePath.includes('Panel')) {
                const panelsDir = path.join(generatedDir, 'panels');
                // 提取类名来确定要删除的文件
                const fileName = path.basename(deletedFilePath, '.ts');
                const possibleGeneratedFiles = [
                    path.join(panelsDir, `${fileName}.ts`),
                    path.join(panelsDir, `${fileName}.js`),
                ];
                possibleGeneratedFiles.forEach(filePath => {
                    if (fs.existsSync(filePath)) {
                        fs.unlinkSync(filePath);
                        console.log(`🗑️  已删除生成文件: ${path.relative(outputDir, filePath)}`);
                    }
                });
                // 重新生成package.json以更新面板配置
                console.log(`🔄 更新配置文件...`);
                this.generateAll(sourceDir, outputDir);
            }
        }
        catch (error) {
            console.error(`❌ 处理文件删除失败: ${error}`);
            // 如果处理失败，重新生成所有文件作为后备方案
            this.generateAll(sourceDir, outputDir);
        }
    }
    static generateAll(sourceDir, outputDir) {
        try {
            const plugins = this.scanPlugins(sourceDir);
            const panels = this.scanPanels(sourceDir);
            if (plugins.length === 0) {
                console.log("⚠️  未找到插件类，请确保使用了@Plugin装饰器");
                return;
            }
            plugins.forEach(plugin => {
                this.generateForPlugin(plugin, panels, outputDir);
            });
            console.log("✅ 自动生成完成！");
        }
        catch (error) {
            console.error("❌ 自动生成失败:", error);
        }
    }
    static scanPlugins(sourceDir) {
        const plugins = [];
        const fs = require('fs');
        const files = this.getTypeScriptFiles(sourceDir);
        files.forEach(file => {
            const content = fs.readFileSync(file, 'utf8');
            const pluginMatch = this.extractDecoratorConfig(content, '@Plugin');
            if (pluginMatch) {
                try {
                    const configStr = pluginMatch.configStr;
                    const nameMatch = configStr.match(/name\s*:\s*["']([^"']+)["']/);
                    const versionMatch = configStr.match(/version\s*:\s*["']([^"']+)["']/);
                    if (nameMatch) {
                        const config = this.parsePluginConfig(configStr);
                        // 扫描 @Menu 装饰器
                        const menuItems = this.scanMenuDecorators(content);
                        if (menuItems.length > 0) {
                            config.menu = menuItems;
                        }
                        plugins.push({
                            name: nameMatch[1],
                            version: (versionMatch === null || versionMatch === void 0 ? void 0 : versionMatch[1]) || '1.0.0',
                            file: file,
                            config: config
                        });
                    }
                }
                catch (error) {
                    console.warn(`解析插件配置失败: ${file}`, error);
                }
            }
        });
        return plugins;
    }
    static scanPanels(sourceDir) {
        const panels = [];
        const fs = require('fs');
        const files = this.getTypeScriptFiles(sourceDir);
        files.forEach(file => {
            const content = fs.readFileSync(file, 'utf8');
            const panelMatch = this.extractDecoratorConfig(content, '@Panel');
            if (panelMatch) {
                try {
                    const configStr = panelMatch.configStr;
                    const titleMatch = configStr.match(/title\s*:\s*["']([^"']+)["']/);
                    const classMatch = content.match(/export\s+class\s+(\w+)\s+extends\s+NexusPanel/);
                    if (titleMatch && classMatch) {
                        panels.push({
                            className: classMatch[1],
                            title: titleMatch[1],
                            file: file,
                            config: this.parsePanelConfig(configStr)
                        });
                    }
                }
                catch (error) {
                    console.warn(`解析面板配置失败: ${file}`, error);
                }
            }
        });
        return panels;
    }
    /**
     * 扫描文件中的 @Menu 装饰器
     */
    static scanMenuDecorators(content) {
        const menuItems = [];
        // 匹配 @Menu 装饰器
        const menuRegex = /@Menu\s*\(\s*["']([^"']+)["']\s*(?:,\s*["']([^"']+)["'])?\s*\)\s*(?:public\s+|private\s+)?(\w+)\s*\(/g;
        let match;
        while ((match = menuRegex.exec(content)) !== null) {
            const path = match[1];
            const label = match[2] || match[3]; // 如果没有提供label，使用方法名
            const action = match[3];
            menuItems.push({
                path: path,
                label: label,
                action: action
            });
        }
        return menuItems;
    }
    static generateForPlugin(plugin, panels, outputDir) {
        var _a, _b, _c, _d;
        const fs = require('fs');
        const path = require('path');
        if (!fs.existsSync(outputDir)) {
            fs.mkdirSync(outputDir, { recursive: true });
        }
        const packageJsonPath = path.join(outputDir, 'package.json');
        let existingPackageJson = {};
        if (fs.existsSync(packageJsonPath)) {
            try {
                const existingContent = fs.readFileSync(packageJsonPath, 'utf8');
                existingPackageJson = JSON.parse(existingContent);
            }
            catch (error) {
                console.warn("⚠️ 读取现有 package.json 失败，将创建新文件");
                existingPackageJson = {};
            }
        }
        const nexusSpecificFields = this.generateNexusSpecificFields(plugin, panels);
        const mergedPackageJson = {
            ...existingPackageJson,
            name: existingPackageJson.name || plugin.name,
            package_version: 2,
            version: existingPackageJson.version || plugin.version,
            description: existingPackageJson.description || `${plugin.name} - 由Nexus框架自动生成`,
            author: existingPackageJson.author || plugin.config.author || "Nexus开发者",
            editor: ">=3.8.0",
            main: "./dist/generated/main.js",
            scripts: {
                ...existingPackageJson.scripts,
                "build": "tsc",
                "watch": "tsc -w"
            },
            devDependencies: existingPackageJson.devDependencies,
            panels: {
                ...existingPackageJson.panels,
                ...nexusSpecificFields.panels
            },
            contributions: {
                menu: [
                    ...(((_a = existingPackageJson.contributions) === null || _a === void 0 ? void 0 : _a.menu) || []),
                    ...(((_b = nexusSpecificFields.contributions) === null || _b === void 0 ? void 0 : _b.menu) || [])
                ].filter((item, index, arr) => arr.findIndex(i => i.message === item.message) === index),
                messages: {
                    ...(_c = existingPackageJson.contributions) === null || _c === void 0 ? void 0 : _c.messages,
                    ...(_d = nexusSpecificFields.contributions) === null || _d === void 0 ? void 0 : _d.messages
                }
            }
        };
        fs.writeFileSync(packageJsonPath, JSON.stringify(mergedPackageJson, null, 2));
        const generatedDir = path.join(outputDir, 'generated');
        if (!fs.existsSync(generatedDir)) {
            fs.mkdirSync(generatedDir, { recursive: true });
        }
        const mainContent = this.generateMainFile(plugin, panels);
        fs.writeFileSync(path.join(generatedDir, 'main.ts'), mainContent);
        console.log("✅ 已生成 generated/main.ts");
        // 3. 生成面板文件（生成到generated/panels目录）
        const panelsDir = path.join(generatedDir, 'panels');
        if (!fs.existsSync(panelsDir)) {
            fs.mkdirSync(panelsDir, { recursive: true });
        }
        // 清理旧的面板文件
        this.cleanupOldPanelFiles(panelsDir, panels);
        // 基于实际扫描到的面板生成文件
        panels.forEach(panel => {
            const panelContent = this.generatePanelFile(panel.className, plugin, panels);
            fs.writeFileSync(path.join(panelsDir, `${panel.className}.ts`), panelContent);
            console.log(`✅ 已生成面板文件 generated/${panel.className}.ts`);
        });
        const tsconfigPath = path.join(outputDir, 'tsconfig.json');
        if (!fs.existsSync(tsconfigPath)) {
            const tsconfig = this.generateTsConfig();
            fs.writeFileSync(tsconfigPath, JSON.stringify(tsconfig, null, 2));
        }
    }
    /**
     * 清理不再存在的面板文件
     */
    static cleanupOldPanelFiles(panelsDir, currentPanels) {
        const fs = require('fs');
        const path = require('path');
        if (!fs.existsSync(panelsDir))
            return;
        try {
            // 获取当前应该存在的面板文件名
            const currentPanelFiles = new Set(currentPanels.map(panel => `${panel.className}.ts`));
            // 扫描现有的生成文件
            const existingFiles = fs.readdirSync(panelsDir);
            existingFiles.forEach((fileName) => {
                if (fileName.endsWith('.ts') && !currentPanelFiles.has(fileName)) {
                    const filePath = path.join(panelsDir, fileName);
                    fs.unlinkSync(filePath);
                    console.log(`🗑️  已清理旧面板文件: generated/${fileName}`);
                }
            });
        }
        catch (error) {
            console.warn(`⚠️ 清理旧面板文件时出错: ${error}`);
        }
    }
    static getTypeScriptFiles(dir) {
        const files = [];
        const fs = require('fs');
        const path = require('path');
        if (!fs.existsSync(dir))
            return files;
        const scan = (currentDir) => {
            const items = fs.readdirSync(currentDir);
            items.forEach((item) => {
                const fullPath = path.join(currentDir, item);
                const stat = fs.statSync(fullPath);
                if (stat.isDirectory()) {
                    scan(fullPath);
                }
                else if (item.endsWith('.ts') && !item.endsWith('.d.ts')) {
                    files.push(fullPath);
                }
            });
        };
        scan(dir);
        return files;
    }
    static parsePluginConfig(configStr) {
        const config = {};
        const nameMatch = configStr.match(/name\s*:\s*["']([^"']+)["']/);
        const versionMatch = configStr.match(/version\s*:\s*["']([^"']+)["']/);
        const authorMatch = configStr.match(/author\s*:\s*["']([^"']+)["']/);
        if (nameMatch)
            config.name = nameMatch[1];
        if (versionMatch)
            config.version = versionMatch[1];
        if (authorMatch)
            config.author = authorMatch[1];
        // 解析 menu 配置
        const menuMatch = configStr.match(/menu\s*:\s*\[([\s\S]*?)\]/);
        if (menuMatch) {
            const menuStr = menuMatch[1];
            config.menu = [];
            const menuItems = menuStr.split('},').map(item => item.trim() + '}');
            menuItems.forEach(itemStr => {
                const pathMatch = itemStr.match(/path\s*:\s*["']([^"']+)["']/);
                const labelMatch = itemStr.match(/label\s*:\s*["']([^"']+)["']/);
                const actionMatch = itemStr.match(/action\s*:\s*["']([^"']+)["']/);
                if (pathMatch && labelMatch && actionMatch) {
                    config.menu.push({
                        path: pathMatch[1],
                        label: labelMatch[1],
                        action: actionMatch[1]
                    });
                }
            });
        }
        const panelsMatch = this.extractObjectFromConfig(configStr, 'panels');
        if (panelsMatch) {
            config.panels = {};
            const panelsStr = panelsMatch;
            const panelNameMatches = panelsStr.match(/"([^"]+)"\s*:/g);
            if (panelNameMatches) {
                panelNameMatches.forEach((nameMatch) => {
                    const panelNameResult = nameMatch.match(/"([^"]+)"/);
                    if (!panelNameResult)
                        return;
                    const panelName = panelNameResult[1];
                    const panelConfigMatch = this.extractObjectFromConfig(panelsStr, `"${panelName}"`);
                    if (panelConfigMatch) {
                        const titleMatch = panelConfigMatch.match(/title\s*:\s*["']([^"']+)["']/);
                        const widthMatch = panelConfigMatch.match(/width\s*:\s*(\d+)/);
                        const heightMatch = panelConfigMatch.match(/height\s*:\s*(\d+)/);
                        const dockableMatch = panelConfigMatch.match(/dockable\s*:\s*(true|false)/);
                        const resizableMatch = panelConfigMatch.match(/resizable\s*:\s*(true|false)/);
                        config.panels[panelName] = {
                            title: titleMatch ? titleMatch[1] : `${panelName} 面板`,
                            width: widthMatch ? parseInt(widthMatch[1]) : 600,
                            height: heightMatch ? parseInt(heightMatch[1]) : 400,
                            dockable: dockableMatch ? dockableMatch[1] === 'true' : true,
                            resizable: resizableMatch ? resizableMatch[1] === 'true' : true
                        };
                    }
                });
            }
        }
        return config;
    }
    static parsePanelConfig(configStr) {
        const config = {};
        const titleMatch = configStr.match(/title\s*:\s*["']([^"']+)["']/);
        const widthMatch = configStr.match(/width\s*:\s*(\d+)/);
        const heightMatch = configStr.match(/height\s*:\s*(\d+)/);
        if (titleMatch)
            config.title = titleMatch[1];
        if (widthMatch)
            config.width = parseInt(widthMatch[1]);
        if (heightMatch)
            config.height = parseInt(heightMatch[1]);
        config.dockable = configStr.includes('dockable: true');
        config.resizable = configStr.includes('resizable: true');
        return config;
    }
    static generateNexusSpecificFields(plugin, panels) {
        var _a;
        return {
            panels: this.generatePanelsConfig(plugin, panels),
            contributions: {
                menu: ((_a = plugin.config.menu) === null || _a === void 0 ? void 0 : _a.map((m) => ({
                    path: m.path,
                    label: m.label,
                    message: m.action
                }))) || [],
                messages: this.generateMessagesConfig(plugin)
            }
        };
    }
    static generatePanelsConfig(plugin, panels) {
        const config = {};
        // 基于实际扫描到的面板生成配置
        panels.forEach(panel => {
            config[panel.className] = {
                title: panel.title || `${plugin.name} 面板`,
                type: panel.config.dockable !== false ? "dockable" : "simple",
                main: `./dist/generated/${panel.className}.js`,
                size: {
                    "min-width": Math.max(300, panel.config.minWidth || 300),
                    "min-height": Math.max(200, panel.config.minHeight || 200),
                    "width": panel.config.width || 600,
                    "height": panel.config.height || 400
                }
            };
        });
        return config;
    }
    /**
     * 生成消息配置
     */
    static generateMessagesConfig(plugin) {
        const config = {};
        if (plugin.config.menu) {
            plugin.config.menu.forEach((menu) => {
                config[menu.action] = {
                    methods: [menu.action]
                };
            });
        }
        return config;
    }
    static actionToMethodName(action) {
        return action.replace(/-([a-zA-Z])/g, (_, letter) => letter.toUpperCase());
    }
    static generateMainFile(plugin, panels) {
        var _a;
        const path = require('path');
        const fs = require('fs');
        const className = this.getClassNameFromFile(plugin.file);
        const isInPluginsDir = plugin.file.includes(path.sep + 'plugins' + path.sep);
        const importPath = isInPluginsDir
            ? `../src/${path.basename(plugin.file, '.ts')}`
            : `../src/${path.basename(plugin.file, '.ts')}`;
        // 检查源文件是否使用默认导出
        const sourceContent = fs.readFileSync(plugin.file, 'utf8');
        const hasDefaultExport = sourceContent.includes(`export default class ${className}`);
        const importStatement = hasDefaultExport
            ? `import ${className} from '${importPath}';`
            : `import { ${className} } from '${importPath}';`;
        return `${importStatement}

let pluginInstance: ${className} | null = null;

export const methods = {
${((_a = plugin.config.menu) === null || _a === void 0 ? void 0 : _a.map((menu) => {
            return `
    '${menu.action}'() {
        try {
            if (pluginInstance) {
                pluginInstance.${this.actionToMethodName(menu.action)}();
            } else {
                console.warn('插件实例未初始化');
            }
        } catch (error) {
            console.error('执行插件方法失败:', error);
        }
    }`;
        }).join(',')) || ''}
};

export function load() {
    try {
        pluginInstance = new ${className}();
        pluginInstance.onLoad();
    } catch (error) {
        console.error('插件加载失败:', error);
    }
}

export function unload() {
    try {
        if (pluginInstance) {
            pluginInstance.onUnload();
            pluginInstance = null;
        }
    } catch (error) {
        console.error('插件卸载失败:', error);
    }
}
`;
    }
    static generatePanelFile(panelName, plugin, panels) {
        var _a, _b, _c, _d;
        const panelInfo = panels.find(p => {
            const className = p.className;
            const isMatch = className === panelName;
            return isMatch;
        });
        const panelClassName = (panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.className) || this.toPascalCase(panelName) + 'Panel';
        const path = require('path');
        const fs = require('fs');
        let importPath = `../../src/${panelClassName}`;
        if (panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.file) {
            // 基于实际文件路径生成import路径
            const relativePath = path.relative(path.join(process.cwd(), 'src'), panelInfo.file);
            importPath = `../../src/${relativePath.replace(/\\/g, '/').replace(/\.ts$/, '')}`;
        }
        else {
            // 如果没有文件信息，假设在src目录下
            importPath = `../../src/${panelClassName}`;
        }
        // 检查面板文件是否使用默认导出
        let hasDefaultExport = false;
        if ((panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.file) && fs.existsSync(panelInfo.file)) {
            const sourceContent = fs.readFileSync(panelInfo.file, 'utf8');
            hasDefaultExport = sourceContent.includes(`export default class ${panelClassName}`);
        }
        const panelImportStatement = hasDefaultExport
            ? `import ${panelClassName} from '${importPath}';`
            : `import { ${panelClassName} } from '${importPath}';`;
        return `${panelImportStatement}
import { NexusGUI } from '@esengine/cocos-nexus';

let panel_${panelName}: ${panelClassName} | null = null;

const template = \`
<div id="nexus-panel-${panelName}" style="width: 100%; height: 100%; background: #1a1a1a; position: relative;">
    <div id="nexus-content-${panelName}" style="width: 100%; height: 100%; background: #2a2a2a;"></div>
</div>
\`;

const style = \`
#nexus-panel-${panelName} {
    background-color: #1a1a1a;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #fff;
    overflow: hidden;
}

#nexus-content-${panelName} {
    position: relative;
}

        /* React渲染系统会自动处理所有UI样式，保持简洁 */
        .nexus-gui-container {
            width: 100%;
            height: 100%;
            overflow: auto;
        }
\`;

const $ = { content: '#nexus-content-${panelName}' };

const panelDefinition = {
    template,
    style,
    $,
    methods: {},
    ready() {
        if (panel_${panelName}) {
            return;
        }
        
        const self = this;
        
        const initPanel = () => {
            try {
                const container = (self.$.content as unknown) as HTMLElement;
                
                if (!container) {
                    console.error('[Nexus] 容器未找到，请检查面板模板');
                    return;
                }
                
                panel_${panelName} = new ${panelClassName}({
                    title: "${(panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.title) || panelName}",
                    width: ${((_a = panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.config) === null || _a === void 0 ? void 0 : _a.width) || 600},
                    height: ${((_b = panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.config) === null || _b === void 0 ? void 0 : _b.height) || 400},
                    dockable: ${((_c = panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.config) === null || _c === void 0 ? void 0 : _c.dockable) !== false},
                    resizable: ${((_d = panelInfo === null || panelInfo === void 0 ? void 0 : panelInfo.config) === null || _d === void 0 ? void 0 : _d.resizable) !== false},
                    // 使用React渲染系统，无需配置渲染选项
                });
                
                panel_${panelName}.onInit();
                
                // 设置容器并开始React渲染
                panel_${panelName}.setContainer(container);
                panel_${panelName}.show();
                
                // 开始渲染 - React系统会自动处理每帧渲染
                try {
                    panel_${panelName}.render();
                } catch (renderError) {
                    console.error('React渲染错误:', renderError);
                }
                
            } catch (error) {
                console.error('面板初始化错误:', error);
            }
        };
        
        const waitForContainer = async () => {
            const existingContainer = (self.$.content as unknown) as HTMLElement;
            if (existingContainer) {
                return existingContainer;
            }
            
            return new Promise<HTMLElement>((resolve, reject) => {
                let attempts = 0;
                const maxAttempts = 300;
                
                const checkContainer = () => {
                    attempts++;
                    
                    const container = (self.$.content as unknown) as HTMLElement;
                    if (container) {
                        resolve(container);
                    } else if (attempts >= maxAttempts) {
                        reject(new Error('容器加载超时'));
                    } else {
                        requestAnimationFrame(checkContainer);
                    }
                };
                
                requestAnimationFrame(checkContainer);
            });
        };
        
        (async () => {
            try {
                const container = await waitForContainer();
                initPanel();
            } catch (error) {
                const err = error as Error;
                console.error('[Nexus] 面板容器加载失败:', err.message);
            }
        })();
    },

    close() {
        try {
            if (panel_${panelName}) {
                panel_${panelName}.onDestroy();
                panel_${panelName} = null;
            }
        } catch (error) {
            console.error('面板关闭错误:', error);
        }
    }
};

module.exports = Editor.Panel.define(panelDefinition);
`;
    }
    static generateTsConfig() {
        return {
            "compilerOptions": {
                "target": "ES2020",
                "module": "CommonJS",
                "lib": ["ES2020", "DOM"],
                "declaration": true,
                "outDir": "./dist",
                "rootDir": "./",
                "strict": true,
                "esModuleInterop": true,
                "experimentalDecorators": true,
                "emitDecoratorMetadata": true,
                "skipLibCheck": true,
                "forceConsistentCasingInFileNames": true,
                "moduleResolution": "node",
                "types": ["node", "@cocos/creator-types/editor"]
            },
            "include": ["src/**/*", "generated/**/*"],
            "exclude": ["node_modules", "dist"]
        };
    }
    static getClassNameFromFile(filePath) {
        const fs = require('fs');
        const path = require('path');
        const content = fs.readFileSync(filePath, 'utf8');
        const match = content.match(/export\s+class\s+(\w+)/);
        return match ? match[1] : path.basename(filePath, '.ts');
    }
    static extractObjectFromConfig(configStr, objectName) {
        const regex = new RegExp(`${objectName}\\s*:\\s*{`, 'g');
        const match = regex.exec(configStr);
        if (!match)
            return null;
        let startIndex = match.index + match[0].length - 1;
        let braceCount = 1;
        let endIndex = startIndex;
        for (let i = startIndex + 1; i < configStr.length; i++) {
            const char = configStr[i];
            if (char === '{') {
                braceCount++;
            }
            else if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                    endIndex = i;
                    break;
                }
            }
        }
        if (braceCount !== 0)
            return null;
        return configStr.substring(startIndex + 1, endIndex);
    }
    static extractDecoratorConfig(content, decoratorName) {
        const regex = new RegExp(`${decoratorName}\\s*\\(\\s*{`, 'g');
        const match = regex.exec(content);
        if (!match)
            return null;
        let startIndex = match.index + match[0].length - 1;
        let braceCount = 1;
        let endIndex = startIndex;
        for (let i = startIndex + 1; i < content.length; i++) {
            const char = content[i];
            if (char === '{') {
                braceCount++;
            }
            else if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                    endIndex = i;
                    break;
                }
            }
        }
        if (braceCount !== 0)
            return null;
        const configStr = content.substring(startIndex + 1, endIndex);
        return { configStr };
    }
    static toPascalCase(str) {
        return str.replace(/(^|-)([a-z])/g, (_, __, letter) => letter.toUpperCase());
    }
    static getCurrentNexusVersion() {
        try {
            const fs = require('fs');
            const path = require('path');
            const possiblePaths = [
                path.join(__dirname, '../../package.json'),
                path.join(__dirname, '../../../package.json'),
                path.join(process.cwd(), 'package.json'),
                path.join(__dirname, '../package.json'),
            ];
            for (const packagePath of possiblePaths) {
                if (fs.existsSync(packagePath)) {
                    try {
                        const packageJson = JSON.parse(fs.readFileSync(packagePath, 'utf8'));
                        if (packageJson.name === '@esengine/cocos-nexus' && packageJson.version) {
                            return `^${packageJson.version}`;
                        }
                    }
                    catch (error) {
                        continue;
                    }
                }
            }
            try {
                const nexusPackageJson = require('@esengine/cocos-nexus/package.json');
                if (nexusPackageJson.version) {
                    return `^${nexusPackageJson.version}`;
                }
            }
            catch (error) {
                // continue
            }
            return 'latest';
        }
        catch (error) {
            return 'latest';
        }
    }
}
exports.NexusAutoGenerator = NexusAutoGenerator;
